import {
  GetEmployeeDetailsCommand,
  GetEmployeeDetailsCommandInput,
  GetEmployeeDetailsOutput,
} from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticationToken } from "../../auth/helpers";
import { setupClaimIntakeClient } from "../setup/setupClaimIntakeClient";
import { MetricsPublisher } from "../../metrics/metrics";

/**
 * Validation code E1900 (INVALID_EID): the service rejects the lookup
 * with this code when the employee ID has no record in PAPI at all —
 * the "associate not found" scenario. Distinct from E0100
 * (ALIAS_MISMATCH), where the associate exists but the alias is wrong.
 */
export const EMPLOYEE_NOT_FOUND_ERROR_CODE = "E1900";

/**
 * True when a rejected lookup carries the E1900 "employee ID not
 * found" validation error. Callers use this to show the
 * "Associate Not Found" guidance instead of the raw service message.
 */
export function isEmployeeNotFoundError(error: unknown): boolean {
  const errors = (error as { errors?: { errorCode?: string }[] } | null | undefined)?.errors;
  return Boolean(errors?.some((detail) => detail?.errorCode === EMPLOYEE_NOT_FOUND_ERROR_CODE));
}

/**
 * Defensive check: not-found lookups normally reject with E1900, but
 * an empty 200 record would otherwise fall through to the misleading
 * "Site not found" message, so callers treat it as associate-not-found
 * too.
 */
export function isAssociateFound(
  employee?: Pick<GetEmployeeDetailsOutput, "firstName" | "lastName" | "homeSite">
): boolean {
  return Boolean(employee && (employee.firstName || employee.lastName || employee.homeSite));
}

export async function getEmployeeDetailsService(input: GetEmployeeDetailsCommandInput) {
  const token = await getAuthenticationToken();
  if (!token) {
    Promise.reject(new Error("Unauthorized"));
  }
  const client = setupClaimIntakeClient(token);
  const command = new GetEmployeeDetailsCommand(input);
  const metricsPublisher = new MetricsPublisher("api.getEmployeeDetails");

  return await client
    .send(command)
    .then((response) => {
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderSuccessMetrics();
      return response;
    })
    .catch((error) => {
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderFatalMetrics();
      throw error;
    });
}
